import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { buildAbsoluteUrl } from 'untils/jsonLdSchemas';
import metaData from './data.json';

const DEFAULT_TWITTER_CARD = 'summary_large_image';
const DEFAULT_IMAGE_URL = buildAbsoluteUrl('/imgs/webp/home/seo.jpg');
const toAbsoluteUrl = (value, fallback = '/') => buildAbsoluteUrl(value || fallback);
const normalizeKeywords = (value, fallback) => {
  const source = value ?? fallback;

  if (Array.isArray(source)) {
    return source.map((keyword) => `${keyword}`.trim()).filter(Boolean);
  }

  if (typeof source === 'string') {
    return source
      .split(',')
      .map((keyword) => keyword.trim())
      .filter(Boolean);
  }

  return [];
};

const HeaderMeta = ({ type, children, jsonLd, overrides }) => {
  const seoMetaData = metaData[type] || metaData['default'];
  const canonicalUrl = toAbsoluteUrl(overrides?.canonicalUrl, window.location.pathname);
  const keywords = normalizeKeywords(overrides?.keywords, seoMetaData.keywords);
  const resolvedMeta = {
    title: overrides?.title || seoMetaData.title,
    description: overrides?.description || seoMetaData.description,
    keywords,
    ogType: overrides?.ogType || 'website',
    ogTitle: overrides?.ogTitle || overrides?.title || seoMetaData.title,
    ogDescription: overrides?.ogDescription || overrides?.description || seoMetaData.description,
    ogImage: toAbsoluteUrl(overrides?.ogImage || overrides?.image, DEFAULT_IMAGE_URL),
    ogUrl: toAbsoluteUrl(overrides?.ogUrl, canonicalUrl),
    twitterCard: overrides?.twitterCard || DEFAULT_TWITTER_CARD,
    twitterTitle:
      overrides?.twitterTitle || overrides?.ogTitle || overrides?.title || seoMetaData.title,
    twitterDescription:
      overrides?.twitterDescription ||
      overrides?.ogDescription ||
      overrides?.description ||
      seoMetaData.description,
    twitterImage: toAbsoluteUrl(
      overrides?.twitterImage || overrides?.ogImage || overrides?.image,
      DEFAULT_IMAGE_URL
    ),
  };

  const jsonLdItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{resolvedMeta.title}</title>
      <meta name="description" content={resolvedMeta.description} />
      <meta name="keywords" content={resolvedMeta.keywords.join(',')} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content={resolvedMeta.ogType} />
      <meta property="og:title" content={resolvedMeta.ogTitle} />
      <meta property="og:description" content={resolvedMeta.ogDescription} />
      <meta property="og:image" content={resolvedMeta.ogImage} />
      <meta property="og:url" content={resolvedMeta.ogUrl} />
      <meta name="twitter:card" content={resolvedMeta.twitterCard} />
      <meta name="twitter:title" content={resolvedMeta.twitterTitle} />
      <meta name="twitter:description" content={resolvedMeta.twitterDescription} />
      <meta name="twitter:image" content={resolvedMeta.twitterImage} />
      {jsonLdItems.map((schema, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      {children}
    </Helmet>
  );
};

HeaderMeta.propTypes = {
  type: PropTypes.oneOf(Object.keys(metaData)).isRequired,
  children: PropTypes.node,
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  overrides: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    keywords: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    canonicalUrl: PropTypes.string,
    ogType: PropTypes.string,
    ogTitle: PropTypes.string,
    ogDescription: PropTypes.string,
    ogImage: PropTypes.string,
    ogUrl: PropTypes.string,
    twitterCard: PropTypes.string,
    twitterTitle: PropTypes.string,
    twitterDescription: PropTypes.string,
    twitterImage: PropTypes.string,
    image: PropTypes.string,
  }),
};

export default HeaderMeta;
